/**
 * EditLead — full-page lead edit form. Same exact layout / styling as
 *   `web-customer/src/routes/CreateLead.tsx` (mirror of the design
 *   folder's create-lead.html), but:
 *     - Pre-filled from `useLead(leadId)` live snapshot.
 *     - Writes via `updateLead(leadId, …)` partial-update helper.
 *     - Title reads "Edit Lead" instead of "New Lead".
 *
 * Reached via the "Edit" button in `LeadSlideOver` or directly by URL
 * (`/leads/{leadId}/edit`).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { canCRMWrite } from '@shared/perms';

import { PagePlaceholder } from '@/components/PagePlaceholder';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { updateLead } from '@/lib/leads';
import {
  BUDGET_RANGE_FORM_OPTIONS,
  POSSESSION_FORM_OPTIONS,
  PRIORITY_FORM_OPTIONS,
  PROJECT_TYPE_FORM_OPTIONS,
  SOURCE_FORM_OPTIONS,
  STATUS_FORM_OPTIONS,
  useLead,
  type BudgetRange,
  type LeadPriority,
  type LeadSource,
  type LeadStatus,
  type PossessionStatus,
  type ProjectType,
} from '@/lib/useLeads';

// ───── Inline styles — copied verbatim from create-lead.html ─────

const STYLE_INPUT: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
};

const STYLE_LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-tertiary)',
  marginBottom: 6,
};

const STYLE_REQUIRED: React.CSSProperties = { color: 'var(--critical)' };

const STYLE_SECTION_TITLE: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  margin: 0,
  marginBottom: 20,
  paddingBottom: 12,
  borderBottom: '1px solid var(--divider)',
  color: 'var(--text-primary)',
};

const STYLE_HEADER_STICKY: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  background: 'var(--page-bg)',
  zIndex: 10,
  paddingBottom: 16,
  borderBottom: '1px solid var(--border)',
  marginBottom: 24,
};

const STYLE_GRID_1_1: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 16,
};

const STYLE_FULL_WIDTH: React.CSSProperties = { gridColumn: '1 / -1' };

// ───── Page ─────

export function EditLeadPage() {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const { lead, loading, error: loadError } = useLead(leadId);
  const { role: orgRole } = useCurrentOrgRole();
  const canWrite = canCRMWrite(orgRole);

  // Section 1 — Lead Details
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [possessionStatus, setPossessionStatus] = useState<PossessionStatus | ''>('');
  const [location, setLocation] = useState('');
  const [projectType, setProjectType] = useState<ProjectType | ''>('');
  const [budgetRange, setBudgetRange] = useState<BudgetRange | ''>('');
  const [source, setSource] = useState<LeadSource | ''>('');

  // Section 2 — Management
  const [status, setStatus] = useState<LeadStatus>('new');
  const [priority, setPriority] = useState<LeadPriority>('medium');
  const [followUpAt, setFollowUpAt] = useState(''); // yyyy-mm-dd
  const [assignedTo, setAssignedTo] = useState('');
  const [notes, setNotes] = useState('');

  // UI
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed when the lead loads.
  useEffect(() => {
    if (!lead) return;
    setName(lead.name ?? '');
    setPhone(lead.phone ?? '');
    setEmail(lead.email ?? '');
    setPossessionStatus(lead.possessionStatus ?? '');
    setLocation(lead.location ?? '');
    setProjectType(lead.projectType ?? '');
    setBudgetRange(lead.budgetRange ?? '');
    setSource(lead.source ?? '');
    setStatus(lead.status ?? 'new');
    setPriority(lead.priority ?? 'medium');
    setFollowUpAt(
      lead.followUpAt
        ? new Date(lead.followUpAt.toMillis()).toISOString().slice(0, 10)
        : '',
    );
    setAssignedTo(lead.assignedTo ?? '');
    setNotes(lead.notes ?? '');
  }, [lead]);

  const handleCancel = () => navigate(`/leads/${leadId ?? ''}`);

  const handleSave = async () => {
    if (!leadId || !lead) return;
    setError(null);
    const tName = name.trim();
    if (tName.length < 2) return setError('Lead name must be at least 2 characters.');

    setBusy(true);
    try {
      await updateLead(leadId, {
        name: tName,
        phone: phone.trim(),
        email: email.trim() ? email.trim() : null,
        source: source || 'other',
        status,
        priority,
        possessionStatus: possessionStatus || null,
        projectType: projectType || null,
        budgetRange: budgetRange || null,
        location: location.trim() ? location.trim() : null,
        followUpAt: followUpAt ? new Date(`${followUpAt}T00:00:00`) : null,
        assignedTo: assignedTo.trim() ? assignedTo.trim() : null,
        notes: notes.trim() ? notes.trim() : null,
      });
      navigate(`/leads/${leadId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save lead.');
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="page active" style={{ display: 'block' }}>
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          Loading lead…
        </div>
      </div>
    );
  }

  if (loadError || !lead) {
    return (
      <div className="page active" style={{ display: 'block' }}>
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>
            <i className="ph ph-warning-circle" />
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
            {loadError ? "Couldn't load lead" : 'Lead not found'}
          </div>
          {loadError ? (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              {loadError}
            </div>
          ) : null}
          <button
            className="btn-primary"
            type="button"
            onClick={() => navigate('/leads')}
          >
            <i className="ph ph-arrow-left" /> Back to Leads
          </button>
        </div>
      </div>
    );
  }

  // Role gate — non-CRM-write roles see a placeholder. Mirrors
  // ProjectEdit pattern; placed after loading/not-found guards so
  // the placeholder doesn't flash during the initial lead-doc load.
  if (orgRole && !canWrite) {
    return (
      <PagePlaceholder
        title="Access denied"
        body="Only Super Admin, Admin, or Manager can edit leads. Ask one of them to make changes."
      />
    );
  }

  return (
    <div className="page active" id="page-edit-lead" style={{ display: 'block' }}>
      {/* ─── Sticky header ─── */}
      <div className="projects-header" style={STYLE_HEADER_STICKY}>
        <div className="projects-title-row">
          <button
            className="btn-ghost"
            style={{ padding: '4px 8px', marginLeft: -8 }}
            type="button"
            onClick={handleCancel}
            disabled={busy}
            aria-label="Back"
          >
            <i className="ph ph-arrow-left" />
          </button>
          <h1>Edit Lead</h1>
        </div>
        <div className="projects-actions">
          <button
            className="btn-secondary"
            type="button"
            onClick={handleCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            className="btn-primary"
            type="button"
            onClick={handleSave}
            disabled={busy}
          >
            <i className="ph-bold ph-check" /> Save changes
          </button>
        </div>
      </div>

      {error ? (
        <div
          className="card"
          style={{
            padding: 12,
            marginBottom: 16,
            borderColor: 'var(--critical)',
            color: 'var(--critical-text)',
            background: 'var(--critical-bg)',
            maxWidth: 800,
          }}
        >
          <i className="ph ph-warning-circle" style={{ marginRight: 6 }} /> {error}
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
        style={{
          maxWidth: 800,
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          marginBottom: 60,
        }}
      >
        {/* ─── Section 1: Lead Details ─── */}
        <div className="card" style={{ padding: 24 }}>
          <h2 style={STYLE_SECTION_TITLE}>Lead Details</h2>
          <div className="responsive-grid-1-1" style={STYLE_GRID_1_1}>
            <div style={STYLE_FULL_WIDTH}>
              <label style={STYLE_LABEL}>
                Name <span style={STYLE_REQUIRED}>*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Amitabh Sharma"
                style={STYLE_INPUT}
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                disabled={busy}
                required
                maxLength={80}
              />
            </div>

            <div>
              <label style={STYLE_LABEL}>Phone</label>
              <input
                type="tel"
                placeholder="+91 99999 99999"
                style={STYLE_INPUT}
                value={phone}
                onChange={(e) => setPhone(e.currentTarget.value)}
                disabled={busy}
              />
            </div>
            <div>
              <label style={STYLE_LABEL}>Email</label>
              <input
                type="email"
                placeholder="client@example.com"
                style={STYLE_INPUT}
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                disabled={busy}
              />
            </div>

            <div>
              <label style={STYLE_LABEL}>Possession Status</label>
              <select
                style={STYLE_INPUT}
                value={possessionStatus}
                onChange={(e) =>
                  setPossessionStatus(e.currentTarget.value as PossessionStatus | '')
                }
                disabled={busy}
              >
                <option value="">— None —</option>
                {POSSESSION_FORM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={STYLE_LABEL}>Location</label>
              <input
                type="text"
                placeholder="e.g. HSR Layout, Sector 2"
                style={STYLE_INPUT}
                value={location}
                onChange={(e) => setLocation(e.currentTarget.value)}
                disabled={busy}
              />
            </div>

            <div>
              <label style={STYLE_LABEL}>Project Type</label>
              <select
                style={STYLE_INPUT}
                value={projectType}
                onChange={(e) =>
                  setProjectType(e.currentTarget.value as ProjectType | '')
                }
                disabled={busy}
              >
                <option value="">— None —</option>
                {PROJECT_TYPE_FORM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={STYLE_LABEL}>Budget</label>
              <select
                style={STYLE_INPUT}
                value={budgetRange}
                onChange={(e) => setBudgetRange(e.currentTarget.value as BudgetRange | '')}
                disabled={busy}
              >
                <option value="">— None —</option>
                {BUDGET_RANGE_FORM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div style={STYLE_FULL_WIDTH}>
              <label style={STYLE_LABEL}>Source</label>
              <select
                style={STYLE_INPUT}
                value={source}
                onChange={(e) => setSource(e.currentTarget.value as LeadSource | '')}
                disabled={busy}
              >
                <option value="">— None —</option>
                {SOURCE_FORM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* ─── Section 2: Management ─── */}
        <div className="card" style={{ padding: 24 }}>
          <h2 style={STYLE_SECTION_TITLE}>Management</h2>
          <div className="responsive-grid-1-1" style={STYLE_GRID_1_1}>
            <div>
              <label style={STYLE_LABEL}>Status</label>
              <select
                style={STYLE_INPUT}
                value={status}
                onChange={(e) => setStatus(e.currentTarget.value as LeadStatus)}
                disabled={busy}
              >
                {STATUS_FORM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={STYLE_LABEL}>Priority</label>
              <select
                style={STYLE_INPUT}
                value={priority}
                onChange={(e) => setPriority(e.currentTarget.value as LeadPriority)}
                disabled={busy}
              >
                {PRIORITY_FORM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={STYLE_LABEL}>Follow-up Date</label>
              <input
                type="date"
                style={STYLE_INPUT}
                value={followUpAt}
                onChange={(e) => setFollowUpAt(e.currentTarget.value)}
                disabled={busy}
              />
            </div>
            <div>
              <label style={STYLE_LABEL}>Assigned To</label>
              <input
                type="text"
                placeholder="Team member name"
                style={STYLE_INPUT}
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.currentTarget.value)}
                disabled={busy}
              />
            </div>

            <div style={{ ...STYLE_FULL_WIDTH, marginTop: 8 }}>
              <label style={STYLE_LABEL}>Note</label>
              <textarea
                placeholder="Client is looking for a modern minimal design..."
                style={{
                  ...STYLE_INPUT,
                  minHeight: 100,
                  resize: 'vertical',
                }}
                value={notes}
                onChange={(e) => setNotes(e.currentTarget.value)}
                disabled={busy}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
